import { h, Fragment } from "preact";
import { CSSTransition } from "react-transition-group";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Close } from "../../helpers/icons";

const SCROLL_LOCK_KEY = "size-guide-modal";
const CELL_MIN_WIDTH = 115;

export default function SizeGuideContent({
  isOpen,
  sizeGuide,
  onClose,
  returnFocusRef,
}) {
  const nodeRef = useRef();
  const tab1 = useRef(null);
  const tab2 = useRef(null);
  const roRef = useRef(null);
  const closeRef = useRef();
  const contentRef = useRef(null);
  const chartsRef = useRef(null);
  const [activeTab, setActiveTab] = useState("tab1");
  const [sideBySideWidth, setSideBySideWidth] = useState(null);

  const sizeCharts = useMemo(
    () =>
      [
        parseTable(
          sizeGuide.sizeChartHeading,
          sizeGuide.sizeChartHeaderRow,
          sizeGuide.sizeChartRows,
        ),
        parseTable(
          sizeGuide.secondarySizeChartHeading,
          sizeGuide.secondarySizeChartHeaderRow,
          sizeGuide.secondarySizeChartRows,
        ),
      ].filter(Boolean),
    [sizeGuide],
  );

  const measurementsChart = useMemo(
    () =>
      parseTable(
        sizeGuide.modelMeasurementsChartHeading,
        sizeGuide.modelMeasurementsHeaderRow,
        sizeGuide.modelMeasurementsRows,
      ),
    [sizeGuide],
  );

  useEffect(() => {
    return () => roRef.current?.disconnect();
  }, []);

  // Size charts sit side by side only when every cell can keep its minimum
  // width; the modal grows past its default max-width to make room.
  const updateChartsLayout = () => {
    if (sizeCharts.length < 2) return;
    if (!nodeRef.current || !contentRef.current || !chartsRef.current) return;

    const { paddingLeft, paddingRight } = getComputedStyle(contentRef.current);
    const gap = parseFloat(getComputedStyle(chartsRef.current).columnGap) || 0;
    // Each table: one 1px grid gap per column plus 1px padding on both sides.
    const chartsWidth =
      sizeCharts.reduce(
        (width, chart) => width + chart.columns * (CELL_MIN_WIDTH + 1) + 1,
        0,
      ) +
      gap * (sizeCharts.length - 1);
    const minWidth =
      chartsWidth + parseFloat(paddingLeft) + parseFloat(paddingRight);

    setSideBySideWidth(
      nodeRef.current.clientWidth >= minWidth ? Math.ceil(minWidth) : null,
    );
  };

  const onEnter = () => {
    window.lockScroll(SCROLL_LOCK_KEY);
    if (nodeRef.current) {
      window.trapFocus(nodeRef.current, closeRef.current);
      roRef.current = new ResizeObserver(updateChartsLayout);
      roRef.current.observe(nodeRef.current);
    }
  };

  const onExited = () => {
    roRef.current?.disconnect();
    window.unlockScroll(SCROLL_LOCK_KEY);
    window.removeTrapFocus(returnFocusRef?.current, nodeRef.current);
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") onClose();
  };

  const showTabs =
    sizeGuide.modelMeasurementsHeaderRow && sizeGuide.modelMeasurementsRows;

  const chartsProps = {
    charts: sizeCharts,
    sideBySide: sideBySideWidth !== null,
    chartsRef,
  };

  return (
    <CSSTransition
      in={isOpen}
      timeout={300}
      classNames="size-guide-content"
      unmountOnExit
      nodeRef={nodeRef}
      onEnter={onEnter}
      onExited={onExited}
    >
      <div
        className="size-guide-content"
        ref={nodeRef}
        onClick={onClose}
        onKeyDown={onKeyDown}
      >
        <div className="size-guide-content__content-wrapper">
          <div
            className="size-guide-content__content"
            ref={contentRef}
            style={{
              "--size-guide-cell-min-width": `${CELL_MIN_WIDTH}px`,
              ...(sideBySideWidth !== null && {
                "--size-guide-min-width": `${sideBySideWidth}px`,
              }),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="size-guide-content__close"
              onClick={onClose}
              ref={closeRef}
            >
              {<Close />}
            </button>
            {sizeGuide.heading && (
              <div className="size-guide-content__heading h4">
                {sizeGuide.heading}
              </div>
            )}
            {showTabs ? (
              <div className="size-guide-content__tabbed-panel-wrapper">
                <div className="size-guide-content__tabbed-buttons-wrapper">
                  <button
                    className={`size-guide-content__tabbed-button ${
                      activeTab === "tab1" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => setActiveTab("tab1")}
                  >
                    {window.localeStrings.product.size_chart}
                  </button>
                  <button
                    className={`size-guide-content__tabbed-button ${
                      activeTab === "tab2" ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => setActiveTab("tab2")}
                  >
                    {window.localeStrings.product.model_measurements}
                  </button>
                </div>
                <CSSTransition
                  in={activeTab === "tab1"}
                  timeout={300}
                  classNames="size-guide-content__tabbed-panels-wrapper"
                  nodeRef={tab1}
                >
                  <div
                    ref={tab1}
                    className="size-guide-content__tabbed-panels-wrapper"
                  >
                    <SizeCharts {...chartsProps} />
                  </div>
                </CSSTransition>

                <CSSTransition
                  in={activeTab === "tab2"}
                  timeout={300}
                  classNames="size-guide-content__tabbed-panels-wrapper"
                  nodeRef={tab2}
                >
                  <div
                    ref={tab2}
                    className="size-guide-content__tabbed-panels-wrapper"
                  >
                    {sizeGuide.modelMeasurementsImage?.image?.url && (
                      <img
                        className="size-guide-content__tabbed-panels-image"
                        src={window.getSizedImageUrl(
                          sizeGuide.modelMeasurementsImage.image.url,
                          "720x",
                        )}
                        alt={
                          sizeGuide.modelMeasurementsImage.image.altText || ""
                        }
                      />
                    )}
                    <div className="size-guide-content__tabbed-panels-chart">
                      <SizeGuidePanel table={measurementsChart} />
                    </div>
                  </div>
                </CSSTransition>
              </div>
            ) : (
              <SizeCharts {...chartsProps} />
            )}
            {sizeGuide.fitNote && (
              <div className="size-guide-content__fit-note body">
                {theme.translations(window.localeStrings.product, "fit_note", {
                  note: sizeGuide.fitNote,
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </CSSTransition>
  );
}

function SizeCharts({ charts, sideBySide, chartsRef }) {
  return (
    <div
      ref={chartsRef}
      className={`size-guide-content__charts${sideBySide ? " size-guide-content__charts--side-by-side" : ""}`}
      style={{
        "--charts-columns": charts
          .map((chart) => `${chart.columns}fr`)
          .join(" "),
      }}
    >
      {charts.map((chart) => (
        <SizeGuidePanel table={chart} />
      ))}
    </div>
  );
}

// Trailing empty cells (from a stray trailing comma) are dropped so a row
// can't add a column and push every following cell out of alignment.
function parseRow(line) {
  const cells = line.split(",").map((cell) => cell.trim());
  while (cells.length > 1 && cells[cells.length - 1] === "") cells.pop();
  return cells;
}

/**
 * Parses a metaobject's comma-separated header row and newline-separated rows.
 * @returns {{ heading, grid: string[][], hasHeaderRow: boolean, columns: number } | null}
 */
function parseTable(heading, rowHeaders, rows) {
  if (!rowHeaders && !rows) return null;

  const headersData = rowHeaders ? parseRow(rowHeaders) : [];
  const rowsData = (rows || "")
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0)
    .map(parseRow);
  const grid = headersData.length ? [headersData, ...rowsData] : rowsData;

  if (grid.length === 0) return null;

  // Rows are `display: contents` in one grid, so a short row would pull the
  // next row's cells up into it — pad every row to the full width.
  const columns = Math.max(...grid.map((row) => row.length));
  grid.forEach((row) => {
    while (row.length < columns) row.push("");
  });

  return { heading, grid, hasHeaderRow: headersData.length > 0, columns };
}

function SizeGuidePanel({ table }) {
  if (!table) return null;

  const { heading, grid, hasHeaderRow, columns } = table;

  return (
    <div
      className="size-guide-content__panel body-2"
      style={{
        "--columns": columns,
        "--rows": grid.length + (heading ? 1 : 0),
      }}
    >
      {heading && (
        <div className="size-guide-content__panel-heading h5">{heading}</div>
      )}
      {grid.map((row, index) => (
        <div
          className={`size-guide-content__panel-row${index % 2 === 0 ? " size-guide-content__panel-row--light" : ""}${index === 0 && hasHeaderRow ? " caption-1" : ""}`}
        >
          {row.map((cell) => (
            <div className="size-guide-content__panel-cell">{cell}</div>
          ))}
        </div>
      ))}
    </div>
  );
}
